import React, { useState, useEffect } from 'react';
import { Card, CardHeader } from '../../components/commons/Card';
import { Box, Button, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Typography, CircularProgress, FormHelperText } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useSelector } from 'react-redux';
import { completeFunderOnboarding } from '../../api/funderOnboarding';
import { validateFunderFormField, validateFunderForm, hasFormErrors } from '../../helpers/funderFormValidation';
import Alert from '../../helpers/alert';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  formRow3: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  submitButton: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  successContainer: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

const FunderOnboarding = () => {
  // ALL HOOKS MUST BE CALLED FIRST, BEFORE ANY CONDITIONAL LOGIC
  const classes = useStyles();
  const auth = useSelector(({ auth }) => auth.tenant);
  
  const [formData, setFormData] = useState({
    // NEW REQUIRED FIELDS
    firstName: '',
    lastName: '',
    personalEmail: '',
    password: '',
    confirmPassword: '',
    businessPhone: '',
    
    // EXISTING BUSINESS FIELDS
    name: '',
    type: '',
    vision: '',
    industries: '',
    website: '',
    contact_email: '',
    fund_size: '',
    min_cheque_size: '',
    max_cheque_size: '',
    fund_stages: '',
    locations: '',
    linkedin: '',
    twitter: '',
    additional_services: '',
    notes: '',
    can_lead_rounds: false,
    is_currently_investing: true,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    
    if (!tokenParam) {
      Alert.error('Invalid onboarding link. Please check your email for the correct link.');
      return;
    }
    
    setToken(tokenParam);
  }, []);

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));

    // Validate field and update errors
    const error = validateFunderFormField(field, value);
    setFieldErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleBlur = (field) => () => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateFunderForm(formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    setFieldErrors({});

    try {
      const response = await completeFunderOnboarding({
        token: token,
        // NEW REQUIRED FIELDS
        firstName: formData.firstName,
        lastName: formData.lastName,
        personalEmail: formData.personalEmail,
        password: formData.password,
        businessPhone: formData.businessPhone,
        
        // EXISTING BUSINESS FIELDS
        name: formData.name,
        type: formData.type,
        vision: formData.vision,
        industries: formData.industries,
        website: formData.website,
        contact_email: formData.contact_email,
        fund_size: formData.fund_size,
        min_cheque_size: formData.min_cheque_size,
        max_cheque_size: formData.max_cheque_size,
        fund_stages: formData.fund_stages,
        locations: formData.locations,
        linkedin: formData.linkedin,
        twitter: formData.twitter,
        additional_services: formData.additional_services,
        notes: formData.notes,
        can_lead_rounds: formData.can_lead_rounds,
        is_currently_investing: formData.is_currently_investing,
      });

      if (response.success) {
        setSuccess(true);
        Alert.success(response.message || 'Onboarding completed successfully! Please check your email to verify your account.');
      } else {
        // Handle backend validation errors (array of error messages)
        if (response.details && Array.isArray(response.details)) {
          const errorMessages = response.details.join(', ');
          Alert.error(`Validation errors: ${errorMessages}`);
          
          // Also set field-specific errors if possible
          const fieldErrors = {};
          response.details.forEach(err => {
            if (err.includes('firstName')) {
              fieldErrors.firstName = 'First Name is required';
            }
            if (err.includes('lastName')) {
              fieldErrors.lastName = 'Last Name is required';
            }
            if (err.includes('personalEmail')) {
              fieldErrors.personalEmail = 'Personal Email is required';
            }
            if (err.includes('password')) {
              fieldErrors.password = 'Password is required';
            }
            if (err.includes('contact_email')) {
              fieldErrors.contact_email = 'Please enter a valid email address';
            }
            if (err.includes('linkedin')) {
              fieldErrors.linkedin = 'Please enter a valid LinkedIn URL';
            }
            if (err.includes('website')) {
              fieldErrors.website = 'Please enter a valid website URL';
            }
            if (err.includes('twitter')) {
              fieldErrors.twitter = 'Please enter a valid Twitter handle';
            }
            if (err.includes('Company/Fund name')) {
              fieldErrors.name = 'Company/Fund name is required';
            }
          });
          setFieldErrors(fieldErrors);
        } else {
          Alert.error(response.error || 'Onboarding failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      // Handle backend validation errors (array of error messages)
      if (Array.isArray(error)) {
        const errorMessages = error.join(', ');
        Alert.error(`Validation errors: ${errorMessages}`);
      } else {
        Alert.error(error.message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // SINGLE RETURN WITH CONDITIONAL RENDERING INSTEAD OF EARLY RETURNS
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Success Message - Show when onboarding is completed */}
      {success && (
        <div className={classes.successContainer}>
          <Typography variant="h5" color="primary" gutterBottom>
            ✅ Onboarding Completed Successfully!
          </Typography>
          <Typography variant="body1">
            You will be connected with the founder shortly.
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            You can close this window now.
          </Typography>
        </div>
      )}

      {/* Loading State - Show when processing */}
      {loading && (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Processing your onboarding...
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            This may take a few moments. Please don't close this window.
          </Typography>
        </div>
      )}

      {/* Main Form - Show when not loading and not success */}
      {!loading && !success && (
        <>
          {/* Header Card */}
          <Card className="mb-6">
            <CardHeader style={{ textAlign: 'center' }}>
              <div className="text-center w-full flex flex-col items-center justify-center">
                <img
                  src={(auth?.logo || {})["2x"]?.path || "/images/logo-v1.png"}
                  alt="InvestorMatch Logo"
                  className="h-24 mx-auto mb-4 block"
                />
                <Typography variant="h4" className="text-center text-primary mb-2">
                  Welcome to InvestorMatch
                </Typography>
                <Typography variant="h6" className="text-center text-gray-600">
                  Complete your onboarding to connect with founders
                </Typography>
              </div>
            </CardHeader>
          </Card>

          {/* Form Card */}
          <Card>
            <CardHeader>
              <Typography variant="h5" className="text-center w-full">Funder Onboarding Form</Typography>
            </CardHeader>
            
            <Box p={3}>
              {/* Form Instructions */}
              <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Note:</strong> Only the Company/Fund Name is required. All other fields are optional and can be filled in later.
                </Typography>
              </Box>

              <form onSubmit={handleSubmit} className={classes.form}>
                {/* Company & Fund Type */}
                <div className={classes.formRow}>
                  <TextField
                    autoFocus
                    label="Company/Fund Name"
                    required
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    onBlur={handleBlur('name')}
                    fullWidth
                    variant="outlined"
                    error={touchedFields.name && !!fieldErrors.name}
                    helperText={touchedFields.name && fieldErrors.name}
                    placeholder="Enter your company or fund name"
                  />
                  <FormControl fullWidth variant="outlined" error={touchedFields.type && !!fieldErrors.type}>
                    <InputLabel>Fund Type (Optional)</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={handleInputChange('type')}
                      onBlur={handleBlur('type')}
                      label="Fund Type (Optional)"
                    >
                      <MenuItem value="">Select fund type</MenuItem>
                      <MenuItem value="VC">Venture Capital</MenuItem>
                      <MenuItem value="Angel">Angel Investor</MenuItem>
                      <MenuItem value="PE">Private Equity</MenuItem>
                      <MenuItem value="Corporate">Corporate VC</MenuItem>
                      <MenuItem value="Family Office">Family Office</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    {touchedFields.type && fieldErrors.type && (
                      <FormHelperText>{fieldErrors.type}</FormHelperText>
                    )}
                  </FormControl>
                </div>

                {/* Personal Information */}
                <div className={classes.formRow}>
                  <TextField
                    label="First Name"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange('firstName')}
                    onBlur={handleBlur('firstName')}
                    fullWidth
                    variant="outlined"
                    error={touchedFields.firstName && !!fieldErrors.firstName}
                    helperText={touchedFields.firstName && fieldErrors.firstName}
                    placeholder="Enter your first name"
                  />
                  <TextField
                    label="Last Name"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange('lastName')}
                    onBlur={handleBlur('lastName')}
                    fullWidth
                    variant="outlined"
                    error={touchedFields.lastName && !!fieldErrors.lastName}
                    helperText={touchedFields.lastName && fieldErrors.lastName}
                    placeholder="Enter your last name"
                  />
                </div>

                {/* Account Credentials */}
                <div className={classes.formRow}>
                  <TextField
                    label="Personal Email"
                    type="email"
                    required
                    value={formData.personalEmail}
                    onChange={handleInputChange('personalEmail')}
                    onBlur={handleBlur('personalEmail')}
                    fullWidth
                    variant="outlined"
                    error={touchedFields.personalEmail && !!fieldErrors.personalEmail}
                    helperText={touchedFields.personalEmail && fieldErrors.personalEmail || "This will be your login email"}
                    placeholder="your@email.com"
                  />
                  <TextField
                    label="Business Phone (Optional)"
                    value={formData.businessPhone}
                    onChange={handleInputChange('businessPhone')}
                    onBlur={handleBlur('businessPhone')}
                    fullWidth
                    variant="outlined"
                    placeholder="+1 (555) 123-4567"
                    error={touchedFields.businessPhone && !!fieldErrors.businessPhone}
                    helperText={touchedFields.businessPhone && fieldErrors.businessPhone}
                  />
                </div>

                {/* Password Fields */}
                <div className={classes.formRow}>
                  <TextField
                    label="Password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    onBlur={handleBlur('password')}
                    fullWidth
                    variant="outlined"
                    error={touchedFields.password && !!fieldErrors.password}
                    helperText={touchedFields.password && fieldErrors.password}
                    placeholder="Create a password"
                    inputProps={{ minLength: 8 }}
                  />
                  <TextField
                    label="Confirm Password"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    onBlur={handleBlur('confirmPassword')}
                    fullWidth
                    variant="outlined"
                    error={touchedFields.confirmPassword && !!fieldErrors.confirmPassword}
                    helperText={touchedFields.confirmPassword && fieldErrors.confirmPassword}
                    placeholder="Confirm your password"
                  />
                </div>

                {/* Contact Information */}
                <div className={classes.formRow}>
                  <TextField
                    label="Contact Email (Optional)"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleInputChange('contact_email')}
                    onBlur={handleBlur('contact_email')}
                    fullWidth
                    variant="outlined"
                    placeholder="business@company.com"
                    error={touchedFields.contact_email && !!fieldErrors.contact_email}
                    helperText={touchedFields.contact_email && fieldErrors.contact_email || "Business contact email (can be different from personal email)"}
                  />
                  <TextField
                    label="Company Website (Optional)"
                    value={formData.website}
                    onChange={handleInputChange('website')}
                    onBlur={handleBlur('website')}
                    fullWidth
                    variant="outlined"
                    placeholder="https://example.com"
                    error={touchedFields.website && !!fieldErrors.website}
                    helperText={touchedFields.website && fieldErrors.website}
                  />
                </div>

                {/* Investment Vision */}
                <TextField
                  label="Investment Vision/Thesis (Optional)"
                  value={formData.vision}
                  onChange={handleInputChange('vision')}
                  onBlur={handleBlur('vision')}
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  error={touchedFields.vision && !!fieldErrors.vision}
                  helperText={touchedFields.vision && fieldErrors.vision}
                  placeholder="Describe your investment strategy and vision..."
                />

                {/* Target Industries */}
                <TextField
                  label="Target Industries (Optional)"
                  value={formData.industries}
                  onChange={handleInputChange('industries')}
                  onBlur={handleBlur('industries')}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="e.g., Technology, Healthcare, Fintech, AI, SaaS..."
                  error={touchedFields.industries && !!fieldErrors.industries}
                  helperText={touchedFields.industries && fieldErrors.industries}
                />

                {/* Fund Size & Investment Range */}
                <div className={classes.formRow3}>
                  <TextField
                    label="Fund Size (Optional)"
                    value={formData.fund_size}
                    onChange={handleInputChange('fund_size')}
                    onBlur={handleBlur('fund_size')}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g., 50,000,000, 100,000,000+"
                    error={touchedFields.fund_size && !!fieldErrors.fund_size}
                    helperText={touchedFields.fund_size && fieldErrors.fund_size}
                  />
                  <TextField
                    label="Minimum Investment (Optional)"
                    value={formData.min_cheque_size}
                    onChange={handleInputChange('min_cheque_size')}
                    onBlur={handleBlur('min_cheque_size')}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g., 100,000, 500,000"
                    error={touchedFields.min_cheque_size && !!fieldErrors.min_cheque_size}
                    helperText={touchedFields.min_cheque_size && fieldErrors.min_cheque_size}
                  />
                  <TextField
                    label="Maximum Investment (Optional)"
                    value={formData.max_cheque_size}
                    onChange={handleInputChange('max_cheque_size')}
                    onBlur={handleBlur('max_cheque_size')}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g., 2,000,000, 5,000,000"
                    error={touchedFields.max_cheque_size && !!fieldErrors.max_cheque_size}
                    helperText={touchedFields.max_cheque_size && fieldErrors.max_cheque_size}
                    />
                </div>

                {/* Investment Stages & Geographic Focus */}
                <div className={classes.formRow}>
                  <FormControl fullWidth variant="outlined" error={touchedFields.fund_stages && !!fieldErrors.fund_stages}>
                    <InputLabel>Target Investment Stages (Optional)</InputLabel>
                    <Select
                      value={formData.fund_stages}
                      onChange={handleInputChange('fund_stages')}
                      onBlur={handleBlur('fund_stages')}
                      label="Target Investment Stages (Optional)"
                    >
                      <MenuItem value="">Select stages</MenuItem>
                      <MenuItem value="Pre-seed">Pre-seed</MenuItem>
                      <MenuItem value="Seed">Seed</MenuItem>
                      <MenuItem value="Series A">Series A</MenuItem>
                      <MenuItem value="Series B">Series B</MenuItem>
                      <MenuItem value="Series C+">Series C+</MenuItem>
                      <MenuItem value="Growth">Growth</MenuItem>
                    </Select>
                    {touchedFields.fund_stages && fieldErrors.fund_stages && (
                      <FormHelperText>{fieldErrors.fund_stages}</FormHelperText>
                    )}
                  </FormControl>
                  <TextField
                    label="Geographic Focus (Optional)"
                    value={formData.locations}
                    onChange={handleInputChange('locations')}
                    onBlur={handleBlur('locations')}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="e.g., North America, Europe, Asia, Global..."
                    error={touchedFields.locations && !!fieldErrors.locations}
                    helperText={touchedFields.locations && fieldErrors.locations}
                  />
                </div>

                {/* Social Links */}
                <div className={classes.formRow}>
                  <TextField
                    label="LinkedIn Profile (Optional)"
                    value={formData.linkedin}
                    onChange={handleInputChange('linkedin')}
                    onBlur={handleBlur('linkedin')}
                    fullWidth
                    variant="outlined"
                    placeholder="https://linkedin.com/in/..."
                    error={touchedFields.linkedin && !!fieldErrors.linkedin}
                    helperText={touchedFields.linkedin && fieldErrors.linkedin}
                  />
                  <TextField
                    label="Twitter Handle (Optional)"
                    value={formData.twitter}
                    onChange={handleInputChange('twitter')}
                    onBlur={handleBlur('twitter')}
                    fullWidth
                    variant="outlined"
                    placeholder="@username"
                    error={touchedFields.twitter && !!fieldErrors.twitter}
                    helperText={touchedFields.twitter && fieldErrors.twitter}
                  />
                </div>

                {/* Additional Services & Notes */}
                <div className={classes.formRow}>
                  <TextField
                    label="Additional Services Offered (Optional)"
                    value={formData.additional_services}
                    onChange={handleInputChange('additional_services')}
                    onBlur={handleBlur('additional_services')}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="e.g., Board seats, operational support, network access, strategic guidance..."
                    error={touchedFields.additional_services && !!fieldErrors.additional_services}
                    helperText={touchedFields.additional_services && fieldErrors.additional_services}
                  />
                  <TextField
                    label="Additional Notes (Optional)"
                    value={formData.notes}
                    onChange={handleInputChange('notes')}
                    onBlur={handleBlur('notes')}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="Any other information you'd like to share..."
                    error={touchedFields.notes && !!fieldErrors.notes}
                    helperText={touchedFields.notes && fieldErrors.notes}
                  />
                </div>

                {/* Checkboxes */}
                <div className={classes.formRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.can_lead_rounds}
                        onChange={handleInputChange('can_lead_rounds')}
                        color="primary"
                      />
                    }
                    label="Can lead investment rounds"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.is_currently_investing}
                        onChange={handleInputChange('is_currently_investing')}
                        color="primary"
                      />
                    }
                    label="Currently actively investing"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  className={classes.submitButton}
                  disabled={loading}
                >
                  Complete Onboarding
                </Button>
              </form>
            </Box>
          </Card>
        </>
      )}
    </div>
  );
};

export default FunderOnboarding;
